import { X, MapPin, FileText, Package, Building2, Truck, ChevronLeft, ChevronRight } from 'lucide-react';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';

const enconOrange = '#ee7203';

const TYPE_CONFIG = {
  order:                 { label: 'SALES ORDER',    bg: '#fff3e0', text: '#e65100', border: '#ffcc80' },
  quote:                 { label: 'QUOTATION',      bg: '#f3e5f5', text: '#6a1b9a', border: '#ce93d8' },
  purchase_order:        { label: 'PURCHASE ORDER', bg: '#e8f5e9', text: '#2e7d32', border: '#a5d6a7' },
  purchase_order_direct: { label: 'DIRECT TO SITE', bg: '#ffebee', text: '#c62828', border: '#ef9a9a' },
  direct_ack:            { label: 'DIRECT ACK',     bg: '#e3f2fd', text: '#1565c0', border: '#90caf9' },
  unknown:               { label: 'DOCUMENT',       bg: '#f5f5f5', text: '#616161', border: '#e0e0e0' },
};

function AddressBlock({ title, address, postcode, icon: Icon, accentColor }) {
  if (!address && !postcode) return null;
  const lines = (address || '').split(/\n/).map(l => l.trim()).filter(Boolean);

  return (
    <div className="rounded-xl border p-4" style={{ borderColor: accentColor + '40', background: accentColor + '08' }}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: accentColor + '20' }}>
          <Icon className="w-3.5 h-3.5" style={{ color: accentColor }} />
        </div>
        <span className="text-xs font-bold uppercase tracking-wide" style={{ color: accentColor }}>{title}</span>
      </div>
      <div className="space-y-0.5">
        {lines.map((line, i) => (
          <p key={i} className={`text-sm ${i === 0 ? 'font-bold text-gray-900' : 'text-gray-600'}`}>{line}</p>
        ))}
        {postcode && !lines.some(l => l === postcode) && (
          <p className="text-sm font-semibold text-gray-800 mt-1">{postcode}</p>
        )}
      </div>
    </div>
  );
}

/**
 * ParsedOrderViewModal
 * Props:
 *   order  — the automation_email document (from /api/automations/orders/:num)
 *   onClose — close handler
 *   inline  — if true, renders as a panel (no overlay) for side-by-side use
 */
export default function ParsedOrderViewModal({ order, onClose, inline = false, allOrderNumbers = [], onNavigateOrder = null, navLoading = false }) {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  if (!order) return null;

  const tc = TYPE_CONFIG[order.email_type] || TYPE_CONFIG.unknown;
  const lines = order.product_lines || [];
  const hasValues = lines.some(l => l.unit_price || l.total_value);
  const orderTotal = lines.reduce((s, l) => s + (parseFloat(l.total_value) || 0), 0);
  const isPO = order.email_type === 'purchase_order' || order.email_type === 'purchase_order_direct';
  // For POs: show supplier name (who Encon ordered FROM), not the invoice customer
  const displayName = isPO
    ? (order.supplier_company || order.customer_name || 'Unknown Supplier')
    : (order.invoice_customer_name || order.customer_name || 'Unknown Customer');

  // Multiple order numbers can be attached to one entry (e.g. a merged/split delivery) —
  // when there's more than one, show arrows + a chip row so the user can flip between
  // each order's parsed details without leaving this panel.
  const hasMultipleOrders = allOrderNumbers.length > 1 && !!onNavigateOrder;
  const currentIdx = allOrderNumbers.findIndex((n) => String(n) === String(order.order_number));
  const goToOffset = (offset) => {
    if (!hasMultipleOrders || navLoading) return;
    const next = allOrderNumbers[(currentIdx + offset + allOrderNumbers.length) % allOrderNumbers.length];
    onNavigateOrder(next);
  };

  const content = (
    <div
      className="bg-white flex flex-col overflow-hidden h-full"
    >
      {/* Header */}
      <div className="flex items-start justify-between px-5 py-4 border-b flex-shrink-0" style={{ borderBottomColor: '#f0f0f0' }}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span
              className="text-[10px] font-bold px-2.5 py-1 rounded-lg uppercase tracking-wide"
              style={{ background: tc.bg, color: tc.text, border: `1px solid ${tc.border}` }}
            >
              {tc.label}
            </span>
            {order.order_number && (
              <span className="font-mono font-bold text-base text-gray-900 bg-gray-100 px-3 py-0.5 rounded-lg">
                #{order.order_number}
              </span>
            )}
            {hasMultipleOrders && (
              <div className="flex items-center gap-1" data-testid="parsed-order-nav-arrows">
                <button
                  onClick={() => goToOffset(-1)}
                  disabled={navLoading}
                  className="w-6 h-6 rounded-md flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-40"
                  title="Previous order"
                  data-testid="parsed-order-prev-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-[10px] text-gray-400 font-semibold">{currentIdx + 1}/{allOrderNumbers.length}</span>
                <button
                  onClick={() => goToOffset(1)}
                  disabled={navLoading}
                  className="w-6 h-6 rounded-md flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-40"
                  title="Next order"
                  data-testid="parsed-order-next-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          {hasMultipleOrders && (
            <div className="flex items-center gap-1.5 flex-wrap mb-1.5" data-testid="parsed-order-chip-row">
              {allOrderNumbers.map((num) => (
                <button
                  key={num}
                  onClick={() => !navLoading && onNavigateOrder(num)}
                  disabled={navLoading}
                  className="text-[11px] font-mono font-semibold px-2 py-0.5 rounded-md border transition-all"
                  style={String(num) === String(order.order_number)
                    ? { background: enconOrange, color: '#fff', borderColor: enconOrange }
                    : { background: '#fff', color: '#6b7280', borderColor: '#e5e7eb' }}
                  data-testid={`parsed-order-chip-${num}`}
                >
                  #{num}
                </button>
              ))}
            </div>
          )}
          <h2 className="font-bold text-gray-900 text-lg leading-tight truncate">
            {displayName}
          </h2>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            {order.reference && (
              <span className="text-xs text-gray-500">Ref: <span className="font-semibold text-gray-700">{order.reference}</span></span>
            )}
            {order.received_at && (
              <span className="text-xs text-gray-400">
                {new Date(order.received_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            )}
            {order.filename && (
              <span className="text-xs text-gray-400 truncate max-w-[160px]">{order.filename}</span>
            )}
          </div>
        </div>
        {!inline && (
          <button onClick={onClose} className="ml-3 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Date-movement history banner (Transport board, via BI Report date changes) */}
      {(order.date_move_history || []).length > 0 && (
        <div
          className="px-5 py-2.5 flex-shrink-0 border-b"
          style={{ background: '#eff6ff', borderBottomColor: '#bfdbfe' }}
          data-testid="date-move-history-banner"
        >
          <div className="flex items-start gap-2">
            <Truck className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-blue-800 uppercase tracking-wide mb-1">
                Delivery date moved on Transport ({order.date_move_history.length})
              </p>
              <div className="space-y-0.5">
                {order.date_move_history.map((m, i) => (
                  <p key={m.id || i} className="text-xs text-blue-700">
                    <span className="font-mono">{m.from_date}</span>
                    {' → '}
                    <span className="font-mono font-semibold">{m.to_date}</span>
                    {m.moved_at && (
                      <span className="text-blue-400"> — {new Date(m.moved_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    )}
                    {m.source === 'bi_report' && <span className="text-blue-400"> (BI Report)</span>}
                    {m.actual_new_delivery_date && (
                      <span className="block text-blue-500 mt-0.5">
                        Real new delivery date is <span className="font-mono font-semibold">{m.actual_new_delivery_date}</span> — flagged here for review since it's over a month out.
                      </span>
                    )}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {/* Addresses — For POs: show DELIVER TO only (Invoice To is Encon's own billing) */}
        <div className="grid grid-cols-1 gap-3" style={{
          gridTemplateColumns: !isPO && order.invoice_address && order.delivery_address ? '1fr 1fr' : '1fr'
        }}>
          {!isPO && (
            <AddressBlock
              title="Invoice To"
              address={order.invoice_address}
              icon={Building2}
              accentColor="#6366f1"
            />
          )}
          <AddressBlock
            title={isPO ? "Delivery Address" : "Deliver To"}
            address={order.delivery_address}
            postcode={order.delivery_postcode}
            icon={Truck}
            accentColor={enconOrange}
          />
        </div>

        {/* Delivery postcode callout (if we have it and no delivery_address) */}
        {!order.delivery_address && order.delivery_postcode && (
          <div className="flex items-center gap-2 p-3 rounded-xl" style={{ background: '#fff3e0', border: '1px solid #ffcc80' }}>
            <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: enconOrange }} />
            <div>
              <p className="text-xs text-orange-600 font-semibold uppercase tracking-wide">Delivery Postcode</p>
              <p className="font-bold text-gray-900">{order.delivery_postcode}</p>
            </div>
          </div>
        )}

        {/* Products table */}
        {lines.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
                Product Lines ({lines.length})
              </span>
            </div>
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-3 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wide">Code</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wide">Description</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wide">Qty</th>
                    {hasValues && <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wide">Unit</th>}
                    {hasValues && <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wide">Total</th>}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, i) => (
                    <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2.5 font-mono font-bold text-gray-800">{line.product_code || '—'}</td>
                      <td className="px-3 py-2.5 text-gray-600 max-w-[180px] truncate">{line.description || '—'}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-gray-700">{line.qty ?? '—'}</td>
                      {hasValues && (
                        <td className="px-3 py-2.5 text-right text-gray-500">
                          {line.unit_price ? `£${parseFloat(line.unit_price).toFixed(2)}` : '—'}
                        </td>
                      )}
                      {hasValues && (
                        <td className="px-3 py-2.5 text-right font-semibold text-gray-700">
                          {line.total_value ? `£${parseFloat(line.total_value).toFixed(2)}` : '—'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                {hasValues && orderTotal > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 bg-gray-50">
                      <td colSpan={hasValues ? 4 : 2} className="px-3 py-2.5 text-right text-xs font-bold text-gray-600 uppercase tracking-wide">
                        Order Total
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold text-gray-900">
                        £{orderTotal.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}

        {/* Contact email */}
        {order.contact_email && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FileText className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{order.contact_email}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      {!inline && (
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
            style={{ backgroundColor: enconOrange }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );

  if (inline) return content;

  return (
    <div
      className="fixed left-0 right-0 bottom-0 flex items-start justify-center pt-[4%]"
      style={{ top: modalUiV2 ? activityBar.headerHeight : 0, zIndex: modalUiV2 ? 45 : 10100, background: modalUiV2 ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width: 'min(96vw, 640px)', maxHeight: '90vh', borderRadius: '1rem', border: `2px solid ${enconOrange}`, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {content}
      </div>
    </div>
  );
}
